import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MdAdd, MdEdit, MdDelete, MdSearch } from 'react-icons/md';
import { FaStar } from 'react-icons/fa';
import { FaQuoteLeft } from 'react-icons/fa';
import axiosInstance from '../../api/axiosInstance';

const TestimonialList = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTestimonials = async () => {
    try {
      const response = await axiosInstance.get('/testimonials');
      setTestimonials(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const filtered = testimonials.filter(
    (t) =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.company || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (window.confirm('Delete this testimonial?')) {
      try {
        await axiosInstance.delete(`/testimonials/${id}`);
        fetchTestimonials();
      } catch (err) {
        alert('Failed to delete testimonial');
      }
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Testimonials</h1>
        <Link
          to="/admin/testimonials/create"
          className="flex items-center justify-center gap-2 bg-[#008300] text-white px-4 py-2 rounded-lg hover:bg-[#006600] transition-colors"
        >
          <MdAdd />
          <span>Add Testimonial</span>
        </Link>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none"
          />
        </div>
      </div>

      {loading && (
        <div className="text-center py-12 text-gray-500">Loading testimonials...</div>
      )}

      {!loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">Client</th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 hidden sm:table-cell">Message</th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">Rating</th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#008300]/10 flex items-center justify-center flex-shrink-0">
                        <FaQuoteLeft className="text-[#008300] text-xs sm:text-sm" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm sm:text-base">{t.name}</p>
                        {t.role && <p className="text-xs text-gray-500">{t.role}</p>}
                        {t.company && <p className="text-xs text-[#008300]">{t.company}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-600 text-sm max-w-[150px] sm:max-w-xs hidden sm:table-cell">
                    <p className="line-clamp-2">{t.message}</p>
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className={`text-xs sm:text-sm ${i < (t.rating || 0) ? 'text-yellow-400' : 'text-gray-200'}`}
                        />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/testimonials/edit/${t.id}`}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <MdEdit />
                      </Link>
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-500">No testimonials found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TestimonialList;
